import { useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { TOWNS, SERVICES } from "../../data/site";
import { track, EVENTS } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  email: "",
  phone: "",
  town: "",
  service: "",
  property_details: "",
  timeline: "",
  message: "",
};

export const QuoteForm = ({
  service = "",
  heading = "Request a Quote",
  submitLabel = "Send My Request",
  type = "quote",
}) => {
  const [form, setForm] = useState({ ...empty, service });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, { ...form, type });
      track(EVENTS.QUOTE_SUBMITTED, { service: form.service || service });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please call or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-confirmation"
        className="cozy-card flex flex-col items-center gap-4 p-10 text-center"
      >
        <CheckCircle2 className="h-14 w-14 text-teal" />
        <h3 className="text-3xl">Thank you — we've got it.</h3>
        <p className="max-w-md text-bodytext">
          Your request is in. <strong className="text-navy">Sarah will follow up
          within 24 hours</strong> to talk through the details and next steps.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="quote-form"
      onSubmit={handleSubmit}
      className="cozy-card p-6 md:p-8"
    >
      <h3 className="text-2xl md:text-3xl">{heading}</h3>
      <p className="mt-1 text-sm text-bodytext/70">
        Response within 24 hours — no pressure, ever.
      </p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Field label="Name" required>
          <Input
            data-testid="quote-name"
            value={form.name}
            onChange={onInput("name")}
            placeholder="Your name"
            required
          />
        </Field>
        <Field label="Email" required>
          <Input
            data-testid="quote-email"
            type="email"
            value={form.email}
            onChange={onInput("email")}
            placeholder="you@email.com"
            required
          />
        </Field>
        <Field label="Phone" required>
          <Input
            data-testid="quote-phone"
            value={form.phone}
            onChange={onInput("phone")}
            placeholder="(781) 555-0123"
            required
          />
        </Field>
        <Field label="Town">
          <Select value={form.town} onValueChange={set("town")}>
            <SelectTrigger data-testid="quote-town" className="rounded-[8px]">
              <SelectValue placeholder="Select your town" />
            </SelectTrigger>
            <SelectContent>
              {TOWNS.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}, MA
                </SelectItem>
              ))}
              <SelectItem value="Other">Other / nearby</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Service">
          <Select value={form.service} onValueChange={set("service")}>
            <SelectTrigger data-testid="quote-service" className="rounded-[8px]">
              <SelectValue placeholder="Which service?" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s.slug} value={s.title}>
                  {s.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Timeline">
          <Input
            data-testid="quote-timeline"
            value={form.timeline}
            onChange={onInput("timeline")}
            placeholder="e.g. next month, ASAP"
          />
        </Field>
      </div>

      <div className="mt-5">
        <Field label="Property details">
          <Input
            data-testid="quote-property"
            value={form.property_details}
            onChange={onInput("property_details")}
            placeholder="Bedrooms, square footage, or anything helpful"
          />
        </Field>
      </div>

      <div className="mt-5">
        <Field label="Anything else?">
          <Textarea
            data-testid="quote-message"
            value={form.message}
            onChange={onInput("message")}
            placeholder="Tell us what would help most."
            rows={4}
          />
        </Field>
      </div>

      <button
        type="submit"
        data-testid="quote-submit"
        disabled={submitting}
        className="btn-teal mt-6 w-full disabled:opacity-60"
      >
        {submitting ? "Sending…" : submitLabel}
      </button>
    </form>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <Label className="mb-1.5 block text-sm font-medium text-navy">
      {label} {required && <span className="text-teal">*</span>}
    </Label>
    {children}
  </div>
);

export default QuoteForm;
